import { useMemo } from 'react';
import { match } from 'ts-pattern';

import type { ContactLanguage } from '~/api/clients/types';
import type { Product } from '~/api/products/types';

import { useProducts } from '~/api/products';
import { useUser } from '~/api/user';

import { useFormWithLinesContext } from '../FormLines.context';
import { getProductName } from '../FormLines.utils';
import { useDragContext } from '../LinesDragDropContext/LinesDragDropContext.context';

/**
 * Find the product linked to the line.
 */
export const useLineProduct = (lineFieldName: `sections.${number}.lines.${number}`) => {
  const { data: products } = useProducts();
  const { watch } = useFormWithLinesContext();

  const watchProductId = watch(`${lineFieldName}.productId`);

  return useMemo(() => products.find((product) => product.id === watchProductId) ?? null, [products, watchProductId]);
};

/**
 * Checks whether the current line is the one that needs to have a border top.
 *
 * Every line has a bottom border as a visual separator. When a line is being dragged, this border is hidden,
 * which means the line below should have a top border added to replace it.
 */
export const useShouldHaveTopBorder = (sectionIndex: number, lineIndex: number): boolean => {
  const { destination, source } = useDragContext();

  // No line is being dragged
  if (!source) return false;

  // Dragging outside of any dropzone
  // This moves all lines up, so there is no need for an extra border
  if (!destination) return false;

  // The line is not being dragged over this section
  if (destination.droppableId !== `${sectionIndex}`) return false;

  return source.droppableId === destination.droppableId
    ? // Same section
      lineIndex <= source.index
      ? destination.index === lineIndex
      : destination.index === lineIndex - 1
    : // Different section
      destination.index === lineIndex;
};

/**
 * Check if a line is free based on the discount options
 */
export const useIsFreeLine = (lineFieldName: `sections.${number}.lines.${number}`): boolean => {
  const { watch } = useFormWithLinesContext();

  const watchDiscountType = watch(`${lineFieldName}.discountType`);
  const watchDiscountValue = watch(`${lineFieldName}.discountValue`) ?? 0;
  const watchPrice = watch(`${lineFieldName}.price`);
  const watchQuantity = watch(`${lineFieldName}.quantity`);

  return match(watchDiscountType)
    .with('percentage', () => watchDiscountValue >= 100)
    .with('flatRate', () => watchDiscountValue >= watchPrice * watchQuantity)
    .with(null, () => false)
    .exhaustive();
};

/**
 * Get the calculated line total, including and excluding discount.
 */
export const useLineTotal = (lineFieldName: `sections.${number}.lines.${number}`): { discountedTotal: number; total: number } => {
  const { watch } = useFormWithLinesContext();

  const watchDiscountType = watch(`${lineFieldName}.discountType`);
  const watchDiscountValue = watch(`${lineFieldName}.discountValue`) ?? 0;
  const watchPrice = watch(`${lineFieldName}.price`);
  const watchQuantity = watch(`${lineFieldName}.quantity`);

  const watchTotal = Math.round(watchPrice * watchQuantity * 100) / 100;
  const watchDiscountedTotal = match(watchDiscountType)
    .with('flatRate', () => Math.min(Math.max(watchTotal - watchDiscountValue, 0), watchTotal))
    .with('percentage', () => Math.min(Math.max(watchTotal - Math.round(watchDiscountValue * watchTotal) / 100, 0), watchTotal))
    .otherwise(() => watchTotal);

  return useMemo(
    () => ({
      total: watchTotal,
      discountedTotal: watchDiscountedTotal,
    }),
    [watchTotal, watchDiscountedTotal],
  );
};

/**
 * Get the translated name of the product in the contact's language,
 * with additional fallbacks in case the translation does not exist.
 */
export const useProductName = (product: Product | null, preferredLanguage: ContactLanguage): string => {
  const { data: user } = useUser();

  return getProductName(product, preferredLanguage, user.lang);
};
